import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Server,
  Database,
  ShieldCheck,
  Power,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Eye,
  Trash2,
  Loader2,
  Download,
  X,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import axiosInstance from '../api/axios';
import { resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import { fetchSoxCycles } from '../request/sox/soxDashboard.api.js';
import { AppSelect } from '../shared/common/AppSelect.tsx';
import {
  DEFAULT_PAGE_SIZE_OPTIONS,
  normalizePageSize,
  readStoredListPageSize,
  writeStoredListPageSize,
} from '../shared/table/pagination.ts';
import { MODULE_KEYS } from '../constants/moduleAccess.ts';
import ModuleAuditDownloadButton from './shared/ModuleAuditDownloadButton.tsx';
import { TablePaginationFooter } from '../shared/table/TablePaginationFooter.tsx';
import ColumnSettingsMenu from './ColumnSettingsMenu';

// ---------------------------------------------------------------------------
// Column config — single source of truth for the table's sortable headers
// and per-row renderers. The user can hide/show + reorder via the gear menu;
// the chosen layout is persisted in localStorage under SYSTEMS_COLS_STORAGE_KEY.
// ---------------------------------------------------------------------------

const SYSTEMS_COLS_STORAGE_KEY = 'sox-systems:cols-v1';

const SYSTEMS_COLUMNS = [
  { key: 'name',    label: 'Name',    sortable: true, align: 'left',   headerClass: 'px-6 py-3 text-left w-[12rem]',   cellClass: 'px-6 py-4 align-top text-sm text-gray-900 font-medium w-[12rem]' },
  { key: 'owner',   label: 'Owner',   sortable: true, align: 'left',   headerClass: 'px-4 py-3 text-left w-[10rem]',   cellClass: 'px-4 py-4 align-top text-sm text-gray-600 w-[10rem]' },
  { key: 'key',     label: 'Key',     sortable: true, align: 'center', headerClass: 'px-4 py-3 text-center w-[6.5rem]', cellClass: 'px-4 py-4 align-middle text-center w-[6.5rem]' },
  { key: 'soc',     label: 'SOC',     sortable: true, align: 'center', headerClass: 'px-4 py-3 text-center w-[5rem]',  cellClass: 'px-4 py-4 align-middle text-center w-[5rem]' },
  { key: 'scope',   label: 'Scope',   sortable: false, align: 'center', headerClass: 'px-4 py-3 text-center w-[7.5rem]', cellClass: 'px-4 py-4 align-middle text-center w-[7.5rem]' },
  { key: 'process', label: 'Process', sortable: false, align: 'left',   headerClass: 'px-4 py-3 text-left w-[14rem]',   cellClass: 'px-4 py-4 align-top text-sm text-gray-600 w-[14rem]' },
  { key: 'status',  label: 'Status',  sortable: true, align: 'center', headerClass: 'px-4 py-3 text-center w-[6.5rem]', cellClass: 'px-4 py-4 align-middle text-center w-[6.5rem]' },
  { key: 'actions', label: 'Actions', sortable: false, align: 'right',  headerClass: 'px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-[5.5rem]', cellClass: 'px-4 py-4 align-top text-right whitespace-nowrap w-[5.5rem]', alwaysVisible: true },
];

/** FE column key → GET /api/systems sort_by (SystemSortableFields). */
const SYSTEMS_SORT_BY_API = {
  name: 'name',
  owner: 'owner',
  key: 'key_system',
  soc: 'soc_report',
  status: 'is_active',
};

const SYSTEMS_DEFAULT_ORDER = SYSTEMS_COLUMNS.map((c) => c.key);
const SYSTEMS_DEFAULT_HIDDEN = [];

function loadColPrefs() {
  if (typeof window === 'undefined') return { order: SYSTEMS_DEFAULT_ORDER, hidden: SYSTEMS_DEFAULT_HIDDEN };
  try {
    const raw = window.localStorage.getItem(SYSTEMS_COLS_STORAGE_KEY);
    if (!raw) return { order: SYSTEMS_DEFAULT_ORDER, hidden: SYSTEMS_DEFAULT_HIDDEN };
    const parsed = JSON.parse(raw);
    const known = new Set(SYSTEMS_DEFAULT_ORDER);
    // Reconcile stored order with current code (drops unknown, appends missing).
    const filteredOrder = Array.isArray(parsed.order) ? parsed.order.filter((k) => known.has(k)) : [];
    for (const k of SYSTEMS_DEFAULT_ORDER) {
      if (!filteredOrder.includes(k)) filteredOrder.push(k);
    }
    const filteredHidden = Array.isArray(parsed.hidden)
      ? parsed.hidden.filter((k) => known.has(k))
      : [];
    return { order: filteredOrder, hidden: filteredHidden };
  } catch (_) {
    return { order: SYSTEMS_DEFAULT_ORDER, hidden: SYSTEMS_DEFAULT_HIDDEN };
  }
}

function saveColPrefs(order, hidden) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SYSTEMS_COLS_STORAGE_KEY, JSON.stringify({ order, hidden }));
  } catch (_) { /* ignore */ }
}

const CHIP_BASE = 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium';
const CHIP_GREEN = `${CHIP_BASE} bg-green-100 text-green-700`;
const CHIP_GRAY = `${CHIP_BASE} bg-gray-100 text-gray-600`;

function getUniqueProcessNames(usedIn) {
  if (!Array.isArray(usedIn) || usedIn.length === 0) return [];
  const seen = new Set();
  const names = [];
  for (const u of usedIn) {
    const key = u?.processId || u?.processName;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    if (u?.processName) names.push(u.processName);
  }
  return names;
}

/** Ellipsis after maxLen chars; full value stays on title/tooltip. */
function truncateChars(value, maxLen = 20) {
  const text = value == null ? '' : String(value);
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen)}...`;
}

function SortIcon({ active, dir }) {
  if (!active) return <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />;
  return dir === 'asc'
    ? <ArrowUp className="h-3.5 w-3.5 text-blue-600" />
    : <ArrowDown className="h-3.5 w-3.5 text-blue-600" />;
}

async function fetchSystemsPage(params) {
  const response = await axiosInstance.get('/api/systems', { params });
  const systems = Array.isArray(response.data?.systems) ? response.data.systems : [];
  const pagination = response.data?.pagination || {};
  return {
    systems,
    totalItems: Number(pagination.total_items) || 0,
    totalPages: Math.max(1, Number(pagination.total_pages) || 1),
  };
}

async function fetchSystemsStats() {
  const response = await axiosInstance.get('/api/systems/stats');
  const d = response.data || {};
  return {
    total: Number(d.total) || 0,
    key: Number(d.key) || 0,
    soc: Number(d.soc) || 0,
    inactive: Number(d.inactive) || 0,
  };
}

/** Walk pages only when Delete all needs every catalog id (not used for the table). */
async function fetchAllSystemsForBulkDelete() {
  const PAGE_SIZE = 100;
  const all = [];
  let page = 1;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { systems, totalPages } = await fetchSystemsPage({ page, page_size: PAGE_SIZE });
    all.push(...systems);
    if (page >= totalPages || systems.length === 0) break;
    page += 1;
  }
  return all;
}

function Systems() {
  const navigate = useNavigate();
  const [systemsData, setSystemsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [systemsPage, setSystemsPage] = useState(1); // 1-based
  const [systemsPageSize, setSystemsPageSize] = useState(readStoredListPageSize);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({ total: 0, key: 0, soc: 0, inactive: 0 });

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [filterCycle, setFilterCycle] = useState('all');
  const [cycleFilterOptions, setCycleFilterOptions] = useState([
    { value: 'all', label: 'All Cycles' },
  ]);
  const [keyFilter, setKeyFilter] = useState('all');         // 'all' | 'key' | 'nonkey'
  const [statusFilter, setStatusFilter] = useState('active'); // 'active' | 'all' | 'inactive'

  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [selectedSystems, setSelectedSystems] = useState([]);
  const [deletingSelected, setDeletingSelected] = useState(false);

  // Column layout state. Hydrated once from localStorage; every change emits
  // a write so prefs persist across reloads. Use `colOrder` for the actual
  // render order; columns in `colHidden` are skipped at render time.
  const [{ order: colOrder, hidden: colHidden }, setColPrefs] = useState(loadColPrefs);
  const updateColPrefs = (nextOrder, nextHidden) => {
    setColPrefs({ order: nextOrder, hidden: nextHidden });
    saveColPrefs(nextOrder, nextHidden);
  };
  const colHiddenSet = useMemo(() => new Set(colHidden), [colHidden]);
  const visibleColumns = useMemo(() => {
    const byKey = new Map(SYSTEMS_COLUMNS.map((c) => [c.key, c]));
    return colOrder.map((k) => byKey.get(k)).filter((c) => c && !colHiddenSet.has(c.key));
  }, [colOrder, colHiddenSet]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cycles = await fetchSoxCycles();
        if (cancelled) return;
        setCycleFilterOptions([
          { value: 'all', label: 'All Cycles' },
          ...cycles
            .map((c) => {
              const value = String(c?.id ?? '').trim();
              if (!value) return null;
              const cycleCode = String(c?.cycle_id ?? '').trim();
              const name = String(c?.name ?? '').trim();
              const label = [cycleCode, name].filter(Boolean).join(' - ') || value;
              return { value, label };
            })
            .filter(Boolean),
        ]);
      } catch {
        if (!cancelled) {
          setCycleFilterOptions([{ value: 'all', label: 'All Cycles' }]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Summary tiles — org catalog totals (deleted_at IS NULL); ignore table filters.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const next = await fetchSystemsStats();
        if (!cancelled) setStats(next);
      } catch (error) {
        console.error('Error fetching systems stats:', error);
        if (!cancelled) setStats({ total: 0, key: 0, soc: 0, inactive: 0 });
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  // Same debounce interval as Risks / Test Runs — drives the API search param.
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearchQuery(searchQuery.trim()), 350);
    return () => window.clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    setSystemsPage(1);
  }, [debouncedSearchQuery, sortKey, sortDir]);

  // Table — one server page (default 10), same pattern as Controls / Risks.
  // Key / status / search / sort are query params on GET /api/systems.
  useEffect(() => {
    let cancelled = false;
    // Keep current rows visible while refetching (filter/page) — spinner only
    // when the table is empty (initial load / hard empty). Avoids blink.
    setLoading((prev) => (systemsData.length === 0 ? true : prev));
    (async () => {
      try {
        const params = {
          page: systemsPage,
          page_size: systemsPageSize,
        };
        if (filterCycle !== 'all') {
          params.sox_cycle_id = filterCycle;
        }
        if (keyFilter === 'key') params.key = 'true';
        if (keyFilter === 'nonkey') params.key = 'false';
        if (statusFilter === 'active' || statusFilter === 'inactive') {
          params.status = statusFilter;
        }
        if (debouncedSearchQuery) {
          params.search = debouncedSearchQuery;
        }
        const apiSortBy = sortKey ? SYSTEMS_SORT_BY_API[sortKey] : null;
        if (apiSortBy) {
          params.sort_by = apiSortBy;
          params.sort_order = sortDir === 'desc' ? 'desc' : 'asc';
        }
        const { systems, totalItems: nextTotal, totalPages: nextPages } = await fetchSystemsPage(params);
        if (cancelled) return;
        setSystemsData(systems);
        setTotalItems(nextTotal);
        setTotalPages(nextPages);
      } catch (error) {
        console.error('Error fetching systems:', error);
        if (!cancelled) {
          setSystemsData([]);
          setTotalItems(0);
          setTotalPages(1);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey, systemsPage, systemsPageSize, filterCycle, keyFilter, statusFilter, debouncedSearchQuery, sortKey, sortDir]);

  useEffect(() => {
    setSystemsPage((prev) => Math.min(Math.max(1, prev), Math.max(1, totalPages)));
  }, [totalPages]);

  // Server returns the sorted page; Scope / Process are not API-sortable.
  const visibleSystems = systemsData;

  const selectedKeySet = useMemo(() => new Set(selectedSystems), [selectedSystems]);
  const pageRowKeys = useMemo(
    () => visibleSystems.map((s) => s.id).filter(Boolean),
    [visibleSystems]
  );
  const allOnPageSelected = useMemo(
    () => pageRowKeys.length > 0 && pageRowKeys.every((k) => selectedKeySet.has(k)),
    [pageRowKeys, selectedKeySet]
  );
  const toggleAllOnPage = useCallback(
    (checked) => {
      if (checked) {
        setSelectedSystems((prev) => {
          const next = new Set(prev);
          pageRowKeys.forEach((k) => next.add(k));
          return Array.from(next);
        });
      } else {
        const pageSet = new Set(pageRowKeys);
        setSelectedSystems((prev) => prev.filter((k) => !pageSet.has(k)));
      }
    },
    [pageRowKeys]
  );
  const toggleSystemKey = useCallback((key, checked) => {
    setSelectedSystems((prev) =>
      checked ? (prev.includes(key) ? prev : [...prev, key]) : prev.filter((x) => x !== key)
    );
  }, []);

  const paginationFrom = totalItems === 0 ? 0 : (systemsPage - 1) * systemsPageSize + 1;
  const paginationTo = Math.min(systemsPage * systemsPageSize, totalItems);

  const handlePageSizeChange = (nextSize) => {
    const normalized = normalizePageSize(nextSize, DEFAULT_PAGE_SIZE_OPTIONS);
    setSystemsPageSize(normalized);
    writeStoredListPageSize(normalized);
    setSystemsPage(1);
  };

  const handleSort = (k) => {
    if (!SYSTEMS_SORT_BY_API[k]) return;
    if (sortKey === k) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir('asc'); }
  };

  const handleDelete = async (system) => {
    if (!system?.id) return;
    if (!window.confirm(`Remove "${system.name}" from the org catalog?`)) return;
    try {
      await axiosInstance.delete(`/api/systems/${system.id}`);
      setSelectedSystems((prev) => prev.filter((id) => id !== system.id));
      setRefreshKey((k) => k + 1);
    } catch (error) {
      console.error('Error deleting system:', error);
      alert(resolve403ErrorMessage(error, 'Failed to remove system.'));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedSystems.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedSystems.length} system(s)?`)) {
      return;
    }

    const selectedData = visibleSystems.filter((s) => selectedKeySet.has(s.id));
    if (selectedData.length === 0) {
      alert(
        'No selected systems are on the current page. Open the page where those rows appear, or clear your selection.'
      );
      return;
    }
    if (selectedData.length !== selectedSystems.length) {
      const proceed = window.confirm(
        `${selectedSystems.length} system(s) are selected, but only ${selectedData.length} are on this page. Delete only those on this page?`
      );
      if (!proceed) return;
    }

    setDeletingSelected(true);
    try {
      for (const system of selectedData) {
        await axiosInstance.delete(`/api/systems/${system.id}`);
      }
      setSelectedSystems([]);
      setRefreshKey((k) => k + 1);
    } catch (error) {
      console.error('Error deleting systems:', error);
      alert(resolve403ErrorMessage(error, 'Failed to delete systems. Please try again.'));
      setRefreshKey((k) => k + 1);
    } finally {
      setDeletingSelected(false);
    }
  };

  const handleExport = () => {
    if (selectedSystems.length === 0) return;

    const selectedData = visibleSystems.filter((s) => selectedKeySet.has(s.id));
    if (selectedData.length === 0) {
      alert(
        'No selected systems are on the current page. Open the page where those rows appear, or clear your selection.'
      );
      return;
    }
    if (selectedData.length !== selectedSystems.length) {
      const proceed = window.confirm(
        `${selectedSystems.length} system(s) are selected, but only ${selectedData.length} are on this page. Export only those on this page?`
      );
      if (!proceed) return;
    }

    // Follow gear menu: visible columns in current order (skip Actions).
    const exportColumns = colOrder
      .filter((key) => key !== 'actions' && !colHidden.includes(key))
      .map((key) => SYSTEMS_COLUMNS.find((c) => c.key === key))
      .filter(Boolean);

    const excelData = selectedData.map((s) => {
      const processNames = getUniqueProcessNames(s.usedIn);
      const inScope = processNames.length > 0;
      // Keep System ID (was always in the prior export).
      const row = {
        'System ID': s.systemId || '',
      };
      for (const col of exportColumns) {
        switch (col.key) {
          case 'name':
            row[col.label] = s.name || '';
            break;
          case 'owner':
            row[col.label] = s.systemOwner || '';
            break;
          case 'key':
            row[col.label] = s.keySystem ? 'Key' : 'Non-key';
            break;
          case 'soc':
            row[col.label] = s.socReport ? 'Yes' : 'No';
            break;
          case 'scope':
            row[col.label] = inScope ? 'In scope' : 'Out of scope';
            break;
          case 'process':
            row[col.label] = processNames.join('; ');
            break;
          case 'status':
            row[col.label] = s.isActive === false ? 'Inactive' : 'Active';
            break;
          default:
            break;
        }
      }
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const colWidthByKey = {
      name: 40,
      owner: 20,
      key: 10,
      soc: 8,
      scope: 14,
      process: 40,
      status: 10,
    };
    worksheet['!cols'] = [
      { wch: 14 }, // System ID
      ...exportColumns.map((col) => ({ wch: colWidthByKey[col.key] || 18 })),
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Systems');
    const timestamp = new Date().toISOString().split('T')[0];
    XLSX.writeFile(workbook, `SOX_Systems_Export_${timestamp}.xlsx`);
  };

  // Hits DELETE /api/systems/:id once per row. There's no server-side bulk
  // endpoint, so the loop is sequential to keep error reporting accurate.
  // Always refetches at the end (refreshKey++) so the table reflects ground
  // truth — surviving rows from any failed deletes show up correctly.
  const handleDeleteAll = async () => {
    let targets = [];
    try {
      targets = await fetchAllSystemsForBulkDelete();
    } catch (error) {
      console.error('Error loading systems for delete-all:', error);
      alert('Failed to load systems for delete-all.');
      return;
    }
    if (targets.length === 0) return;
    if (!window.confirm(
      `Permanently remove ALL ${targets.length} system(s) from the org catalog?\n\n`
      + `This cannot be undone.`
    )) return;
    if (!window.confirm(
      `Final check — deleting ${targets.length} system(s). Proceed?`
    )) return;
    setBulkDeleting(true);
    let failures = 0;
    for (const s of targets) {
      try {
        await axiosInstance.delete(`/api/systems/${s.id}`);
      } catch (error) {
        console.error(`Failed to delete system ${s.name}:`, error);
        failures += 1;
      }
    }
    setBulkDeleting(false);
    if (failures > 0) {
      alert(`Deleted ${targets.length - failures} of ${targets.length}. ${failures} failed — see console.`);
    }
    setSelectedSystems([]);
    setSystemsPage(1);
    setRefreshKey((k) => k + 1);
  };

  const handleResetFilters = () => {
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setFilterCycle('all');
    setKeyFilter('all');
    setStatusFilter('active');
    setSystemsPage(1);
  };

  const filtersActive = searchQuery !== '' || filterCycle !== 'all' || keyFilter !== 'all' || statusFilter !== 'active';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Systems</h1>
            <p className="text-sm text-gray-600 mt-1">
              Applications and platforms in scope for testing. Link to ITGC coverage to drive chips and filters on the ITGC matrix.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ModuleAuditDownloadButton
              resourceType="system"
              moduleKey={MODULE_KEYS.SYSTEMS}
              filenamePrefix="system_audit"
            />
            <button
              type="button"
              onClick={handleDeleteAll}
              disabled={bulkDeleting || loading || stats.total === 0}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="Permanently remove every system from the org catalog"
            >
              {bulkDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Delete all
            </button>
            <button
              type="button"
              onClick={() => navigate('/sox-systems/new')}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Create system
            </button>
          </div>
        </div>

      

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Systems</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                <Server className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Key</p>
                <p className="text-2xl font-bold text-green-600">{stats.key}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">SOC on file</p>
                <p className="text-2xl font-bold text-blue-600">{stats.soc}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                <Database className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Inactive</p>
                <p className="text-2xl font-bold text-gray-900">{stats.inactive}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                <Power className="w-5 h-5 text-gray-500" />
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search systems by name or owner..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <AppSelect
              wrapperClassName="w-44 shrink-0"
              size="control"
              value={filterCycle}
              onChange={(e) => {
                setFilterCycle(e.target.value);
                setSystemsPage(1);
              }}
              aria-label="Filter by cycle"
              options={cycleFilterOptions}
            />
            <AppSelect
              value={keyFilter}
              onChange={(e) => {
                setKeyFilter(e.target.value);
                setSystemsPage(1);
              }}
              aria-label="Filter by key system"
              options={[
                { label: 'All Systems', value: 'all' },
                { label: 'Key only', value: 'key' },
                { label: 'Non-key', value: 'nonkey' },
              ]}
            />
            <AppSelect
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setSystemsPage(1);
              }}
              aria-label="Filter by status"
              options={[
                { label: 'Active', value: 'active' },
                { label: 'All Statuses', value: 'all' },
                { label: 'Inactive', value: 'inactive' },
              ]}
            />
            <button
              type="button"
              onClick={handleResetFilters}
              disabled={!filtersActive}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Reset Filters
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-3">
            {selectedSystems.length > 0 ? (
              <div className="flex flex-wrap items-center gap-2 min-w-0">
                <span className="text-sm font-medium text-blue-900">
                  {selectedSystems.length} {selectedSystems.length === 1 ? 'item' : 'items'} selected of {totalItems}{' '}
                  {totalItems === 1 ? 'system' : 'systems'}
                </span>
                <button
                  type="button"
                  onClick={handleExport}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-md hover:bg-blue-50 transition-colors"
                  aria-label="Export selected"
                >
                  <Download className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={handleDeleteSelected}
                  disabled={deletingSelected}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50"
                  aria-label="Delete selected"
                >
                  {deletingSelected ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedSystems([])}
                  className="p-1 text-blue-700 hover:text-blue-900 transition-colors"
                  aria-label="Cancel selections"
                  title="Cancel selections"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <h2 className="text-lg font-medium text-gray-900">
                All Systems ({totalItems})
              </h2>
            )}
            <ColumnSettingsMenu
              columns={SYSTEMS_COLUMNS}
              order={colOrder}
              hidden={colHidden}
              defaults={{ order: SYSTEMS_DEFAULT_ORDER, hidden: SYSTEMS_DEFAULT_HIDDEN }}
              onChange={updateColPrefs}
            />
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full table-fixed divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10 shadow-[inset_0_-1px_0_0_rgba(0,0,0,0.05)]">
                <tr>
                  <th scope="col" className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      className="h-4 w-4 cursor-pointer rounded border border-gray-300 text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                      onChange={(e) => toggleAllOnPage(e.target.checked)}
                      checked={allOnPageSelected}
                      disabled={loading || pageRowKeys.length === 0}
                      aria-label="Select all systems on this page"
                    />
                  </th>
                  {visibleColumns.map((col) => (
                    <th key={col.key} scope="col" className={`${col.headerClass} whitespace-nowrap`}>
                      {col.sortable ? (
                        <button
                          type="button"
                          onClick={() => handleSort(col.key)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-800"
                        >
                          {col.label} <SortIcon active={sortKey === col.key} dir={sortDir} />
                        </button>
                      ) : (
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {col.label}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={visibleColumns.length + 1} className="px-6 py-12 text-center">
                      <Loader2 className="h-6 w-6 text-blue-600 animate-spin mx-auto mb-2" />
                      <p className="text-sm text-gray-500">Loading systems…</p>
                    </td>
                  </tr>
                ) : visibleSystems.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns.length + 1} className="px-6 py-12 text-center">
                      <Server className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm text-gray-500">No systems match the current filters.</p>
                    </td>
                  </tr>
                ) : visibleSystems.map((s) => {
                  const processNames = getUniqueProcessNames(s.usedIn);
                  const inScope = processNames.length > 0;
                  const processSummary = processNames.length ? processNames.join(', ') : '—';
                  const ownerText = s.systemOwner || '—';
                  const hasCanonicalRow = Boolean(s.id);
                  const rowKey = s.id || s.systemId || s.name;
                  // Per-column renderer. SOC is intentionally simplified to a
                  // Yes/No chip (no SOC 1 / SOC 2 sub-chips) per the listing
                  // requirements.
                  const renderCell = (col) => {
                    switch (col.key) {
                      case 'name':
                        return (
                          <td key={col.key} className={col.cellClass} title={s.name || ''}>
                            {truncateChars(s.name, 20)}
                          </td>
                        );
                      case 'owner':
                        return (
                          <td key={col.key} className={col.cellClass} title={ownerText}>
                            {truncateChars(ownerText === '—' ? '' : ownerText, 20) || '—'}
                          </td>
                        );
                      case 'key':
                        return (
                          <td key={col.key} className={col.cellClass}>
                            <span className={s.keySystem ? CHIP_GREEN : CHIP_GRAY}>{s.keySystem ? 'Key' : 'Non-key'}</span>
                          </td>
                        );
                      case 'soc':
                        return (
                          <td key={col.key} className={col.cellClass}>
                            <span className={s.socReport ? CHIP_GREEN : CHIP_GRAY}>{s.socReport ? 'Yes' : 'No'}</span>
                          </td>
                        );
                      case 'scope':
                        return (
                          <td key={col.key} className={col.cellClass}>
                            <span className={inScope ? CHIP_GREEN : CHIP_GRAY}>{inScope ? 'In scope' : 'Out of scope'}</span>
                          </td>
                        );
                      case 'process':
                        return (
                          <td key={col.key} className={col.cellClass} title={processSummary}>
                            <p className="line-clamp-2">{processSummary}</p>
                          </td>
                        );
                      case 'status':
                        return (
                          <td key={col.key} className={col.cellClass}>
                            <span className={s.isActive === false ? CHIP_GRAY : CHIP_GREEN}>
                              {s.isActive === false ? 'Inactive' : 'Active'}
                            </span>
                          </td>
                        );
                      case 'actions':
                        return (
                          <td key={col.key} className={col.cellClass}>
                            <div className="inline-flex items-center gap-1 justify-end">
                              <button
                                type="button"
                                title={hasCanonicalRow ? 'View' : 'View (no catalog entry yet)'}
                                disabled={!hasCanonicalRow}
                                onClick={() => navigate(`/sox-systems/${s.id}`)}
                                className="inline-flex items-center justify-center rounded p-1.5 text-gray-600 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                title={hasCanonicalRow ? 'Remove from catalog' : 'Remove (no catalog entry yet)'}
                                disabled={!hasCanonicalRow}
                                onClick={() => handleDelete(s)}
                                className="inline-flex items-center justify-center rounded p-1.5 text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        );
                      default:
                        return <td key={col.key} className={col.cellClass}>—</td>;
                    }
                  };
                  return (
                    <tr key={rowKey} className="hover:bg-gray-50">
                      <td className="px-4 py-4 align-middle">
                        <input
                          type="checkbox"
                          className="h-4 w-4 cursor-pointer rounded border border-gray-300 text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                          checked={Boolean(s.id) && selectedKeySet.has(s.id)}
                          disabled={!hasCanonicalRow}
                          onChange={(e) => {
                            if (!s.id) return;
                            toggleSystemKey(s.id, e.target.checked);
                          }}
                          aria-label="Select system"
                        />
                      </td>
                      {visibleColumns.map(renderCell)}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!loading && totalItems > 0 ? (
            <TablePaginationFooter
              from={paginationFrom}
              to={paginationTo}
              total={totalItems}
              itemLabel="systems"
              pageIndex={Math.max(0, systemsPage - 1)}
              pageCount={totalPages}
              canPreviousPage={systemsPage > 1}
              canNextPage={systemsPage < totalPages}
              onPreviousPage={() => setSystemsPage((p) => Math.max(1, p - 1))}
              onNextPage={() => setSystemsPage((p) => Math.min(totalPages, p + 1))}
              showPageSizeSelector
              pageSize={systemsPageSize}
              pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
              onPageSizeChange={handlePageSizeChange}
            />
          ) : null}
        </div>
      </div>

    </div>
  );
}

export default Systems;
